/**
 * HEXA skill effect text for the skill-icon tooltips (`components/hexa/HexaSkillTooltip.tsx`).
 *
 * `public/hexa-skill-info.json` (vendored dev-side by `scripts/fetch-hexa-skill-info.mjs`, served
 * same-origin — no runtime game-API calls) stores each skill's wiki `SkillTable` fields: a static
 * `description` and a `formula` string with MediaWiki level expressions inline, e.g.
 * "Damage: {#expr:232+8*x}%" where x = the node's level. `resolveFormula` evaluates those spans
 * for a concrete level with a tiny arithmetic parser — never `eval` — and renders `?` for any
 * expression using syntax beyond it (a handful of wiki formulas use custom notation, e.g. Sol
 * Janus's milestone pseudo-functions; honest "?" beats a guessed number for patch-sensitive game
 * data).
 */
import { useQuery } from '@tanstack/react-query';

export interface HexaSkillInfo {
  description: string | null;
  formula: string | null;
}

interface HexaSkillInfoFile {
  skills: Record<string, HexaSkillInfo>;
}

export function useHexaSkillInfo() {
  return useQuery({
    queryKey: ['hexa-skill-info'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const res = await fetch('/hexa-skill-info.json', { signal });
      if (!res.ok) throw new Error(`hexa-skill-info.json: HTTP ${res.status}`);
      return (await res.json()) as HexaSkillInfoFile;
    },
    // Static bundled asset — never refetch during a session.
    staleTime: Infinity,
    retry: 1,
  });
}

/**
 * Recursive-descent evaluator for the arithmetic subset the wiki formulas actually use:
 * numbers, `x`, `+ - * /`, parentheses, and `floor(...)`/`ceil(...)`/`round(...)`/`trunc(...)`.
 * Returns null for anything outside that subset (unknown function names, comparison operators,
 * wiki-custom notation) so the caller can render `?` instead of a wrong number.
 */
export function evaluateExpr(expr: string, x: number): number | null {
  const src = expr.replace(/\s+/g, '');
  let pos = 0;

  function parseExpression(): number | null {
    let value = parseTerm();
    if (value === null) return null;
    while (pos < src.length && (src[pos] === '+' || src[pos] === '-')) {
      const op = src[pos];
      pos += 1;
      const rhs = parseTerm();
      if (rhs === null) return null;
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  }

  function parseTerm(): number | null {
    let value = parseFactor();
    if (value === null) return null;
    while (pos < src.length && (src[pos] === '*' || src[pos] === '/')) {
      const op = src[pos];
      pos += 1;
      const rhs = parseFactor();
      if (rhs === null) return null;
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  }

  function parseFactor(): number | null {
    if (src[pos] === '-') {
      pos += 1;
      const value = parseFactor();
      return value === null ? null : -value;
    }
    if (src[pos] === '(') {
      pos += 1;
      const value = parseExpression();
      if (value === null || src[pos] !== ')') return null;
      pos += 1;
      return value;
    }
    const fn = /^(floor|ceil|round|trunc)\(/.exec(src.slice(pos));
    if (fn) {
      pos += fn[0].length;
      const value = parseExpression();
      if (value === null || src[pos] !== ')') return null;
      pos += 1;
      return Math[fn[1] as 'floor' | 'ceil' | 'round' | 'trunc'](value);
    }
    if (src[pos] === 'x') {
      pos += 1;
      return x;
    }
    const num = /^\d+(?:\.\d+)?/.exec(src.slice(pos));
    if (num) {
      pos += num[0].length;
      return Number(num[0]);
    }
    return null;
  }

  const result = parseExpression();
  if (result === null || pos !== src.length || !Number.isFinite(result)) return null;
  return result;
}

/** Substitutes every `{#expr:...}` span in a vendored formula string with its value at `level`
 *  (`?` when the expression is outside `evaluateExpr`'s subset). */
export function resolveFormula(formula: string, level: number): string {
  return formula.replace(/\{#expr:([^}]*)\}/g, (_, expr: string) => {
    const value = evaluateExpr(expr, level);
    if (value === null) return '?';
    return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
  });
}
